import { SYNCABLE_FEE_KINDS, type SyncableFeeKind } from '@dallali/shared';

/**
 * Bilingual labels per syncable fee kind (CEO: 410101 Brokerage).
 *
 * Record-keyed on the shared `SYNCABLE_FEE_KINDS`, so a kind added to the API
 * scope guard fails compile here until it gets a label — the UI cannot silently
 * drift from what the server will actually sync.
 *
 * Lives here rather than beside either consumer because BOTH the super-admin
 * fee-account map and the per-invoice sync badge render these, and a second copy
 * would diverge the first time a kind is renamed.
 */
export const FEE_TYPE_LABELS: Record<
  SyncableFeeKind,
  { ar: string; en: string }
> = {
  brokerage_fee: { ar: 'رسوم السعي', en: 'Brokerage Fee' },
  marketing_fee: { ar: 'رسوم التسويق', en: 'Marketing Fee' },
  property_management_fee: {
    ar: 'رسوم إدارة الأملاك',
    en: 'Property Management Fee',
  },
  contract_fee: { ar: 'رسوم العقد', en: 'Contract Fee' },
  management_fee: { ar: 'رسوم الإدارة', en: 'Management Fee' },
  ejar_fee: { ar: 'رسوم إيجار', en: 'Ejar Fee' },
  subscription_fee: { ar: 'رسوم الاشتراك', en: 'Subscription Fee' },
  monthly_maintenance: { ar: 'صيانة شهرية', en: 'Monthly Maintenance' },
};

/** Syncable Dallali fee kinds → Qoyod GL account-code rows, in shared order. */
export const FEE_TYPES: { key: SyncableFeeKind; ar: string; en: string }[] =
  SYNCABLE_FEE_KINDS.map((key) => ({ key, ...FEE_TYPE_LABELS[key] }));

/**
 * Label for a fee kind the SERVER classified.
 *
 * Takes a bare string, not `SyncableFeeKind`: classification happens server-side
 * and can return a kind this build has no label for (a newly added kind, an
 * older client). Falling back to the raw value keeps the badge readable instead
 * of blank — the kind is still information, just untranslated.
 */
export function feeKindLabel(
  kind: string,
  t: (ar: string, en: string) => string
): string {
  const label = FEE_TYPE_LABELS[kind as SyncableFeeKind];
  return label ? t(label.ar, label.en) : kind;
}
